/**
 * Secure Cookie Management Utility
 * Handles cookies across all environments (dev, staging, production)
 */

/**
 * Clear all cookies for the current domain and all parent domains
 * Works across localhost, staging, and production environments
 */
export const clearAllCookies = () => {
  const cookies = document.cookie.split(';');
  const hostname = window.location.hostname;
  const pathBits = window.location.pathname.split('/');
  
  // Get all possible domains to clear cookies from
  const domains = [
    hostname,
    `.${hostname}`,
    '',  // No domain (current domain only)
  ];
  
  // Add parent domains if not localhost
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    const parts = hostname.split('.');
    // For example: app.example.com -> [.example.com, .com]
    for (let i = 1; i < parts.length; i++) {
      domains.push(`.${parts.slice(i).join('.')}`);
    }
  }
  
  // Get all possible paths
  const paths = ['/'];
  for (let i = 1; i <= pathBits.length; i++) {
    paths.push(pathBits.slice(0, i).join('/') || '/');
  }
  
  // Clear each cookie with all possible domain and path combinations
  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    
    if (!cookieName) return;
    
    // Try clearing with all domain and path combinations
    domains.forEach(domain => {
      paths.forEach(path => {
        // Standard clear
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
        
        // Clear with SameSite and Secure
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=None; Secure`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=Lax`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=Strict`;
      });
    });
    
    // Also try without domain attribute (for current domain)
    paths.forEach(path => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
    });
  });
};

/**
 * Clear specific cookie by name
 * @param {string} cookieName - Name of the cookie to clear
 */
export const clearCookie = (cookieName) => {
  const hostname = window.location.hostname;
  const pathBits = window.location.pathname.split('/');
  
  const domains = [
    hostname,
    `.${hostname}`,
    '',
  ];
  
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    const parts = hostname.split('.');
    for (let i = 1; i < parts.length; i++) {
      domains.push(`.${parts.slice(i).join('.')}`);
    }
  }
  
  const paths = ['/'];
  for (let i = 1; i <= pathBits.length; i++) {
    paths.push(pathBits.slice(0, i).join('/') || '/');
  }
  
  domains.forEach(domain => {
    paths.forEach(path => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=None; Secure`;
    });
  });
  
  paths.forEach(path => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
  });
};

/**
 * Get cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie ? document.cookie.split(';') : [];
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      const value = cookie.substring(nameEQ.length, cookie.length);
      return value === '' ? null : value;
    }
  }
  return null;
};

/**
 * Set a non-auth cookie with proper security attributes.
 * NOTE: Auth cookies (access_token, refresh_token) are HttpOnly and
 * managed exclusively by the server. This function is for non-auth
 * cookies only (e.g., user preferences, UI state).
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Days until expiration (default: 7)
 * @param {object} options - Additional options (path, domain, secure, sameSite)
 */
export const setCookie = (name, value, days = 7, options = {}) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  
  const {
    path = '/',
    domain = '',
    secure = window.location.protocol === 'https:',
    sameSite = 'Lax'
  } = options;
  
  let cookie = `${name}=${value}; expires=${date.toUTCString()}; path=${path}`;
  
  if (domain) {
    cookie += `; domain=${domain}`;
  }
  
  if (secure) {
    cookie += '; Secure';
  }
  
  cookie += `; SameSite=${sameSite}`;
  
  document.cookie = cookie;
};

/**
 * Clear all storage and cookies (comprehensive cleanup)
 */
export const clearAllStorage = () => {
  // Clear localStorage
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
  }
  
  // Clear sessionStorage
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Failed to clear sessionStorage:', error);
  }
  
  // Clear cookies
  clearAllCookies();
  
  // Clear IndexedDB (if used)
  if (window.indexedDB) {
    try {
      window.indexedDB.databases().then(databases => {
        databases.forEach(db => {
          window.indexedDB.deleteDatabase(db.name);
        });
      });
    } catch (error) {
      console.error('Failed to clear IndexedDB:', error);
    }
  }
};

/**
 * Get the appropriate redirect URL based on environment
 */
export const getRedirectURL = () => {
  // Use environment variable if available
  if (process.env.REACT_APP_FRONTEND_URL) {
    return process.env.REACT_APP_FRONTEND_URL;
  }
  
  // Otherwise use current origin
  return window.location.origin;
};

export default {
  clearAllCookies,
  clearCookie,
  getCookie,
  setCookie,
  clearAllStorage,
  getRedirectURL
};
